import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import SendIcon from '@mui/icons-material/SendOutlined';
import {
  ApplicationForm,
  approveApplication,
  chooseCounselorMethod,
  getLeadApplicationForm,
  rejectApplication,
  sendApplicationInvitation,
} from '../../api/applicationForm';
import { getPortalInviteLink } from '../../api/students';
import { Step11Review } from '../students/wizard/Step11Review';
import { APPLICATION_FORM_STATUS_LABEL } from '../portal/applicationFormStatus';
import { describeNumber } from '../../components/temporaryNumber';

const errorText = (err: unknown) =>
  isAxiosError(err) ? ((err.response?.data as { message?: string } | undefined)?.message ?? 'Something went wrong.') : 'Something went wrong.';
const when = (value: string | null | undefined) => (value ? new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const AWAITING_REVIEW = ['SUBMITTED', 'RESUBMITTED'];

/** Review a student's submitted application: the application as it stands,
 * every submission and decision so far, Approve, or Reject with a reason. */
export const ApplicationReviewDialog = ({
  form,
  open,
  onClose,
  onDecided,
}: {
  form: ApplicationForm;
  open: boolean;
  onClose: () => void;
  onDecided: (form: ApplicationForm) => void;
}) => {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const close = () => {
    setRejecting(false);
    setReason('');
    approve.reset();
    reject.reset();
    onClose();
  };
  const approve = useMutation({ mutationFn: () => approveApplication(form.studentId!), onSuccess: (f) => (onDecided(f), close()) });
  const reject = useMutation({ mutationFn: () => rejectApplication(form.studentId!, reason.trim()), onSuccess: (f) => (onDecided(f), close()) });
  const busy = approve.isPending || reject.isPending;
  const awaiting = AWAITING_REVIEW.includes(form.status);

  return (
    <Dialog open={open} onClose={busy ? undefined : close} maxWidth="md" fullWidth>
      <DialogTitle>
        Review application {describeNumber(form.applicationNumber, 'application')}{' '}
        <Chip size="small" color="primary" label={APPLICATION_FORM_STATUS_LABEL[form.status]} sx={{ ml: 1 }} />
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {form.submissions.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Submissions
              </Typography>
              <Table size="small" aria-label="Submissions">
                <TableBody>
                  {form.submissions.map((s) => (
                    <TableRow key={s.version}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Version {s.version}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Submitted {when(s.submittedAt)}</TableCell>
                      <TableCell>
                        {s.decision ? (
                          <>
                            <strong>{s.decision === 'APPROVED' ? 'Approved' : 'Rejected'}</strong> by {s.decidedBy ?? '—'} · {when(s.decidedAt)}
                            {s.reason ? ` — ${s.reason}` : ''}
                          </>
                        ) : (
                          'Awaiting review'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary">
                Each submission is kept exactly as the student sent it.
              </Typography>
            </Box>
          )}
          {form.studentId && <Step11Review studentId={form.studentId} />}
          {rejecting && (
            <TextField
              label="Reason for rejection"
              helperText="Required — the student sees this and corrects the application."
              multiline
              minRows={2}
              autoFocus
              fullWidth
              inputProps={{ maxLength: 1000 }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
          {(approve.error || reject.error) && <Alert severity="error">{errorText(approve.error ?? reject.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          Close
        </Button>
        {awaiting && !rejecting && (
          <>
            <Button color="error" onClick={() => setRejecting(true)} disabled={busy}>
              Reject
            </Button>
            <Button variant="contained" color="success" onClick={() => approve.mutate()} disabled={busy}>
              {approve.isPending ? 'Approving…' : 'Approve application'}
            </Button>
          </>
        )}
        {awaiting && rejecting && (
          <>
            <Button onClick={() => setRejecting(false)} disabled={busy}>
              Back
            </Button>
            <Button variant="contained" color="error" onClick={() => reject.mutate()} disabled={busy || !reason.trim()}>
              {reject.isPending ? 'Rejecting…' : 'Reject application'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

/** Personal Appointment → how the University Application Form is completed
 * (2026-11-06): the student in the Student Portal (send the form —
 * invitation, status, resend, review), or the counsellor with the existing
 * form ("Counselor Completes Application", which `onCounselorMethod` opens).
 * Both fill the same application. */
export const LeadApplicationFormPanel = ({
  leadId,
  canManage,
  onCounselorMethod,
  onChanged,
}: {
  leadId: number;
  canManage: boolean;
  onCounselorMethod: () => void;
  onChanged: () => void;
}) => {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['lead-application-form', leadId], queryFn: () => getLeadApplicationForm(leadId) });
  const [notice, setNotice] = useState<{ severity: 'success' | 'warning'; text: string } | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const refresh = (form?: ApplicationForm) => {
    if (form) queryClient.setQueryData(['lead-application-form', leadId], form);
    void queryClient.invalidateQueries({ queryKey: ['lead-application-form', leadId] });
    onChanged();
  };

  const invite = useMutation({
    mutationFn: () => sendApplicationInvitation(leadId),
    onSuccess: (r) => {
      refresh(r.form);
      setLink(null);
      setNotice(
        r.invite.status === 'SENT'
          ? { severity: 'success', text: `Application form sent to ${r.form.portalAccount?.loginEmail ?? 'the student'} — a secure, single-use link to set their portal password.` }
          : {
              severity: 'warning',
              text: `The portal login is ready, but the email could not be sent${r.invite.errorMessage ? `: ${r.invite.errorMessage}` : ''}. Copy the link and send it to the student yourself.`,
            },
      );
    },
  });
  const copyLink = useMutation({
    mutationFn: () => getPortalInviteLink(query.data!.studentId!),
    onSuccess: async (r) => {
      setLink(r.link);
      await navigator.clipboard?.writeText(r.link).catch(() => undefined);
    },
  });
  const counselor = useMutation({
    mutationFn: () => chooseCounselorMethod(query.data!.studentId!),
    onSuccess: (f) => {
      refresh(f);
      onCounselorMethod();
    },
  });

  const form = query.data;
  if (!form) return null;
  const awaiting = AWAITING_REVIEW.includes(form.status);
  const portal = form.method === 'STUDENT_PORTAL';
  const canSend = canManage && !awaiting && form.status !== 'APPROVED';

  return (
    <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }} data-testid="application-form-panel">
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          Application form
        </Typography>
        <Chip size="small" color={form.status === 'APPROVED' ? 'success' : form.status === 'REJECTED' ? 'error' : awaiting ? 'warning' : 'default'} label={APPLICATION_FORM_STATUS_LABEL[form.status]} />
        {form.method && <Chip size="small" variant="outlined" label={portal ? 'Student completes (portal)' : 'Counsellor completes'} />}
      </Stack>
      {form.status === 'REJECTED' && form.rejectionReason && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
          Changes requested: {form.rejectionReason}
        </Typography>
      )}
      {portal && form.invitedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Invitation sent {when(form.invitedAt)}
          {form.portalAccount ? ` to ${form.portalAccount.loginEmail} · ${form.portalAccount.passwordSet ? 'portal password set' : 'password not set yet'}` : ''}
          {form.submittedAt ? ` · last submitted ${when(form.submittedAt)}` : ''}
        </Typography>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
        {awaiting && (
          <Button size="small" variant="contained" color="warning" onClick={() => setReviewOpen(true)}>
            Review application
          </Button>
        )}
        {canSend && (
          <Button size="small" variant={portal ? 'outlined' : 'contained'} startIcon={<SendIcon />} disabled={invite.isPending} onClick={() => invite.mutate()}>
            {portal ? 'Resend application form' : 'Send Student Application Form'}
          </Button>
        )}
        {canManage && portal && form.studentId && !awaiting && form.status !== 'APPROVED' && (
          <Button size="small" startIcon={<ContentCopyIcon />} disabled={copyLink.isPending} onClick={() => copyLink.mutate()}>
            Copy portal link
          </Button>
        )}
        {canManage && !awaiting && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => (portal && form.studentId ? counselor.mutate() : onCounselorMethod())}
            disabled={counselor.isPending}
          >
            Counselor Completes Application
          </Button>
        )}
        {form.status === 'APPROVED' && form.studentId && (
          <Button size="small" onClick={() => setReviewOpen(true)}>
            View application
          </Button>
        )}
      </Stack>
      {notice && (
        <Alert severity={notice.severity} sx={{ mt: 1 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}
      {link && (
        <Alert severity="info" sx={{ mt: 1 }} onClose={() => setLink(null)}>
          Link copied — it works once and expires in 7 days: <span style={{ wordBreak: 'break-all' }}>{link}</span>
        </Alert>
      )}
      {(invite.error || copyLink.error || counselor.error) && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {errorText(invite.error ?? copyLink.error ?? counselor.error)}
        </Alert>
      )}
      <ApplicationReviewDialog form={form} open={reviewOpen} onClose={() => setReviewOpen(false)} onDecided={(f) => refresh(f)} />
    </Box>
  );
};
